import {useTranslation} from 'react-i18next';
import {Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, MenuItem, Typography} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import * as React from "react";
import {useContext, useEffect, useState} from "react";
import {
    ID_BE_FREE_EVO,
    ID_CAR_BOX,
    ID_CAR_CLOUD, ID_DAMAGE_SHEET,
    ID_FLEX_RENT,
    ID_TRANSFERS
} from "../../mobilitystore/constants/PrintoutTypes";
import * as Yup from 'yup';
import {HighlightOffRounded} from "@mui/icons-material";
import {STATE_FIXED_VOICE_TABLE_UPDATING} from '../constants/ClustersRecoil'
import {useSetRecoilState} from "recoil";
import {SubmitHandler, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import FormSelect from "../../../experiments/forms/FormSelect";
import FormTextField from "../../../experiments/forms/FormTextField";
import {useClusterFixedVoiceRepository} from "../../../api/repositories/ClusterFixedVoiceRepository";
import {DAMAGE_SHEET_RATE, TRANSFER_RATE, VEHICLE_DELIVERY, VEHICLE_REPLACEMENT} from "../constants/EventTypes";
import {setSnackbar} from "../../../../../util/state/globalAction";
import {snackbarTypes} from "../../../../../util/state/globalVariables";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";
import {DECIMAL_REG_EXP} from "../../../utils/FilterUtils";

interface ModalProps {
    open: boolean,
    onClose: Function,
    type: string,
    fixedVoices?: any,
    clusterId: number,
    legalEntity: string,
    dealer: string
}

interface FixedVoicesInput {
    printoutType: string,
    settingsType: string,
    raAcrissCode: string,
    raSippCode: string,
    valueRate: number,
    fromKm: number,
    toKm: number
}

const FixedVoicesModal = (props: ModalProps) => {
    const {t} = useTranslation('finance', {keyPrefix: 'clusters'});
    const {dispatchGlobalData} = useContext(GlobalContext);

    const {createFixedVoices, updateFixedVoices} = useClusterFixedVoiceRepository();
    const setUpdating = useSetRecoilState(STATE_FIXED_VOICE_TABLE_UPDATING);
    const [transferVoice, setTransferVoice] = useState(props?.fixedVoices?.printoutType);

    const printoutTypes = [
        {
            label: t("printout-types." + ID_BE_FREE_EVO),
            value: ID_BE_FREE_EVO,
            selected: true
        },
        {
            label: t("printout-types." + ID_CAR_CLOUD),
            value: ID_CAR_CLOUD
        },
        {
            label: t("printout-types." + ID_FLEX_RENT),
            value: ID_FLEX_RENT
        },
        {
            label: t("printout-types." + ID_CAR_BOX),
            value: ID_CAR_BOX
        },
        {
            label: t("printout-types." + ID_TRANSFERS),
            value: ID_TRANSFERS
        },
        {
            label: t("printout-types." + ID_DAMAGE_SHEET),
            value: ID_DAMAGE_SHEET
        }
    ];

    const eventType = [
        {
            label: t("settings-types." + VEHICLE_DELIVERY),
            value: VEHICLE_DELIVERY,
            selected: true
        },
        {
            label: t("settings-types." + VEHICLE_REPLACEMENT),
            value: VEHICLE_REPLACEMENT
        }
    ];

    const schema = Yup.object().shape({
        printoutType: Yup.string().required(),
        settingsType: Yup.string().required(),
        raAcrissCode: Yup.string().test("raAcrissCode", t('filters.errors.missingAcriss'), (value, context) => {
            const raSippCode = context.parent.raSippCode;
            if (!value && !raSippCode) {
                return true;
            }
            if (value && !raSippCode) {
                return true;
            }
            return !!(value && raSippCode);

        }),
        raSippCode: Yup.string().nullable(),
        valueRate: Yup.number().required().typeError(t('filters.errors.invalidNumber'))
            .test("is-decimal", t('filters.errors.invalidDecimal'), (value) => {
                return value !== undefined && DECIMAL_REG_EXP.test(value.toString());
            }),
    });

    const schemaTransfer = Yup.object().shape({
        printoutType: Yup.string().required(),
        settingsType: Yup.string().required(),
        valueRate: Yup.number().required().typeError(t('filters.errors.invalidNumber'))
            .test("is-decimal", t('filters.errors.invalidDecimal'), (value) => {
                return value !== undefined && DECIMAL_REG_EXP.test(value.toString());
            }),
        fromKm: Yup.number().when('printoutType', {
            is: ID_TRANSFERS ,
            then: Yup.number().required().typeError(t('filters.errors.invalidNumber')),
        }).nullable(),

        toKm: Yup.number().when('printoutType', {
            is: ID_TRANSFERS ,
            then: Yup.number().required().typeError(t('filters.errors.invalidNumber')),
        }).nullable(),

    });


    const defaultValues = {
        printoutType: '',
        settingsType: '',
        raAcrissCode: undefined,
        raSippCode: undefined,
        valueRate: undefined,
        fromKm: undefined,
        toKm: undefined
    };

    const retrieveInitialValues = () => {
        if (props.fixedVoices && props.type === 'edit' && props.open) {
            return {
                printoutType: props.fixedVoices.printoutType,
                settingsType: props.fixedVoices.printoutType === ID_TRANSFERS ? t("settings-types." + TRANSFER_RATE) : props.fixedVoices.settingsType,
                raAcrissCode: props.fixedVoices.raAcrissCode,
                raSippCode: props.fixedVoices.raSippCode,
                valueRate: props.fixedVoices.valueRate,
                fromKm: props.fixedVoices.fromKm,
                toKm: props.fixedVoices.toKm
            }
        } else {
            return defaultValues;
        }
    }

    const {control, reset, watch, setValue, clearErrors, handleSubmit, formState} = useForm<FixedVoicesInput>({
        defaultValues: retrieveInitialValues(),
        resolver: (transferVoice === ID_TRANSFERS || transferVoice === ID_DAMAGE_SHEET)? yupResolver(schemaTransfer) : yupResolver(schema)
    });


    const onSubmit: SubmitHandler<FixedVoicesInput> = data => {
        let isNotSubscription = data.printoutType === ID_TRANSFERS || data.printoutType === ID_DAMAGE_SHEET
        if (props.type === 'add') {
            createFixedVoices(props.clusterId, {
                printoutType: data.printoutType,
                settingsType: data.printoutType === ID_TRANSFERS ? TRANSFER_RATE : data.printoutType === ID_DAMAGE_SHEET ? DAMAGE_SHEET_RATE : data.settingsType,
                raAcrissCode: isNotSubscription ? undefined : data.raAcrissCode !== "" ? data.raAcrissCode : undefined,
                raSippCode: isNotSubscription ? undefined : data.raSippCode !== "" ? data.raSippCode : undefined,
                valueRate: data.valueRate,
                fromKm: data.printoutType === ID_TRANSFERS ? data.fromKm : undefined,
                toKm: data.printoutType === ID_TRANSFERS ? data.toKm : undefined,
                legalEntity: props.legalEntity,
                dealer: props.dealer
            }).then((res) => {
                if (res.status === 'KO') {
                    dispatchGlobalData({
                        type: setSnackbar,
                        payload: {
                            snackbar: {
                                message: t('error-dialog.errors.' + res.message),
                                type: snackbarTypes.error,
                                show: true,
                                autoClose: true,
                            },
                        },
                    });
                } else {
                    props.onClose();
                    cleanForm();
                    setUpdating(true)
                }
            });
        } else {
            if (props.fixedVoices !== undefined && props.fixedVoices.id !== undefined) {
                updateFixedVoices(props.clusterId, props.fixedVoices.id, {
                    printoutType: data.printoutType,
                    settingsType: data.printoutType === ID_TRANSFERS ? TRANSFER_RATE : data.printoutType === ID_DAMAGE_SHEET ? DAMAGE_SHEET_RATE : data.settingsType,
                    raAcrissCode: isNotSubscription ? undefined : data.raAcrissCode !== "" ? data.raAcrissCode : undefined,
                    raSippCode: isNotSubscription ? undefined : data.raSippCode !== "" ? data.raSippCode : undefined,
                    valueRate: data.valueRate,
                    fromKm: data.printoutType === ID_TRANSFERS ? data.fromKm : undefined,
                    toKm: data.printoutType === ID_TRANSFERS ? data.toKm : undefined,
                }).then((res) => {
                    if (res.status === 'KO') {
                        dispatchGlobalData({
                            type: setSnackbar,
                            payload: {
                                snackbar: {
                                    message: t('error-dialog.errors.' + res.message),
                                    type: snackbarTypes.error,
                                    show: true,
                                    autoClose: true,
                                },
                            },
                        });
                    } else {
                        props.onClose();
                        cleanForm();
                        setUpdating(true)
                    }
                });
            }
        }
    }

    useEffect(() => {
        reset(retrieveInitialValues())
    }, [props?.fixedVoices, props.open])

    useEffect(() => {
        setTransferVoice(props?.fixedVoices?.printoutType)
    }, [props?.fixedVoices?.printoutType])


    const cleanForm = () => {
        reset(defaultValues);
        clearErrors();
    }

    return (
        <Dialog maxWidth={"lg"} disableEscapeKeyDown fullWidth={true} open={props.open} onClose={() => {
            return;
        }}>
            <DialogTitle>
                <Typography fontWeight={"bold"} color={"primary"}>
                    {props.type === 'edit' ? t('fixed-voice-modal.title.edit') + " " +
                        props.fixedVoices?.id : t('fixed-voice-modal.title.add')}
                </Typography>
                <IconButton
                    aria-label="close"
                    onClick={() => {
                        props.onClose();
                        cleanForm();
                    }}
                    color={"primary"}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                    }}
                >
                    <HighlightOffRounded/>
                </IconButton>
            </DialogTitle>
            <form onSubmit={handleSubmit(onSubmit, (errors, event) => console.log(errors))}>
                <DialogContent>
                    <Grid container spacing={2} sx={{padding: "15px"}} alignItems={"center"}>
                        <Grid item md={3} sm={6} xs={12}>
                            <FormSelect
                                control={control}
                                name="printoutType"
                                props={{
                                    onChange: (e: any) => {
                                        setTransferVoice(e.target.value)
                                        if(e.target.value === ID_TRANSFERS) {
                                            setValue('settingsType', t("settings-types." + TRANSFER_RATE) ,  {shouldTouch: true})
                                        } else if(e.target.value === ID_DAMAGE_SHEET) {
                                            setValue('settingsType', t("settings-types." + DAMAGE_SHEET_RATE) ,  {shouldTouch: true})
                                        }
                                    },
                                    style: {width: "100%"},
                                    label: t('fixed-voice-modal.form.printout-type')
                                }}
                                items={printoutTypes.map((option: any) => (
                                    <MenuItem key={option.value}
                                              value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            />
                        </Grid>
                        {(watch("printoutType") === ID_TRANSFERS || watch("printoutType") === ID_DAMAGE_SHEET) ?
                            <Grid item md={3} sm={6} xs={12}>
                                <FormTextField
                                    control={control}
                                    name={'settingsType'}
                                    props={{
                                        sx: {width: '100%'},
                                        label: t('fixed-voice-modal.form.settings-type'),
                                        type: 'string',
                                        disabled: true
                                    }}/>
                            </Grid>
                            :
                            <Grid item md={3} sm={6} xs={12}>
                                <FormSelect
                                    name="settingsType"
                                    control={control}
                                    props={{
                                        style: {width: "100%"},
                                        label: t('fixed-voice-modal.form.settings-type')
                                    }}
                                    items={eventType.map((option: any,) => (
                                        <MenuItem key={option.value}
                                                  value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                />
                            </Grid>
                        }

                        <Grid item md={3} sm={6} xs={12}>
                            <FormTextField
                                control={control}
                                name={'raAcrissCode'}
                                props={{
                                    sx: {
                                        width: '100%',
                                        visibility: (watch("printoutType") === ID_TRANSFERS || watch("printoutType") === ID_DAMAGE_SHEET) ? "hidden" : "visible"
                                    },
                                    label: t('fixed-voice-modal.form.acriss'),
                                    type: 'string'
                                }}/>
                        </Grid>
                        <Grid item md={3} sm={6} xs={12}>
                            <FormTextField
                                control={control}
                                name={'raSippCode'}
                                props={{
                                    sx: {
                                        width: '100%',
                                        visibility: (watch("printoutType") === ID_TRANSFERS || watch("printoutType") === ID_DAMAGE_SHEET) ? "hidden" : "visible"
                                    },
                                    label: t('fixed-voice-modal.form.sipp'),
                                    type: 'string'
                                }}/>
                        </Grid>

                        <Grid item md={3} sm={6} xs={12}>
                            <FormTextField
                                control={control}
                                name={'valueRate'}
                                props={{
                                    sx: {width: '100%'},
                                    label: t('fixed-voice-modal.form.valueRate'),
                                    type: 'number'
                                }}
                            />
                        </Grid>
                        <Grid item md={3} sm={6} xs={12}>
                            <FormTextField
                                control={control}
                                name={'fromKm'}
                                props={{
                                    sx: {
                                        width: '100%',
                                        visibility: watch("printoutType") !== ID_TRANSFERS ? "hidden" : "visible"
                                    },
                                    label: t('fixed-voice-modal.form.fromKm'),
                                    type: 'number'
                                }}
                            />
                        </Grid>
                        <Grid item md={3} sm={6} xs={12}>
                            <FormTextField
                                control={control}
                                name={'toKm'}
                                props={{
                                    sx: {
                                        width: '100%',
                                        visibility: watch("printoutType") !== ID_TRANSFERS ? "hidden" : "visible"
                                    },
                                    label: t('fixed-voice-modal.form.toKm'),
                                    type: 'number'
                                }}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{padding: "10px"}}>
                    <Button variant={"outlined"} color={"accent"}
                            onClick={() => {
                                props.onClose();
                                cleanForm();
                            }}>{t('fixed-voice-modal.buttons.reset')}</Button>
                    <Button color={"accent"} type="submit">{t('fixed-voice-modal.buttons.apply')}</Button>
                </DialogActions>
            </form>
        </Dialog>
    )
}
export default FixedVoicesModal;